import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import EmptyList from "go-core/components/EmptyList";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as LoadingSVG } from "go-core/images/svg/loading.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { hasPermission } from "go-security/services/organizations/selectors";
import Segment from "go-segment/components";
import { SegmentType } from "go-segment/components/types";
import { ReportLastUpdateInfo } from "../../Common/ReportLastUpdateInfo/ReportLastUpdateInfo";
import useDashboardApi from "../services/hooks/useDashboardApi";
import useDashboardContext from "../services/hooks/useDashboardContext";
import { useDashboardSegment } from "../services/hooks/useDashboardSegment";
import useIsFirstRender from "../services/hooks/useIsFirstRender";
import { DashboardType } from "../services/types";
import { DraggableDashboardMainContent } from "./Draggable/DraggableDashboardMainContent";
import { EditModeTriggerButton } from "./Draggable/EditModeTriggerButton";
import DashboardFilters from "./Filter/DashboardFilters";
import DashboardTabs, { tabType } from "./Tabs/DashboardTabs";

const Dashboard = (): JSX.Element => {
	const [showSegmentModal, setShowSegmentModal] = useState(false);
	const [activeTab, setActiveTab] = useState<tabType>("GENERAL");
	const { fetchDataAndApplyNewDataToDashboardState, loading, rerenderDashboard } = useDashboardApi();
	const {
		state,
		state: {
			segmentsConfig: { allSegments, selectedSegment },
		},
		dashboardType,
		isEditMode,
	} = useDashboardContext();
	const isFirstRender = useIsFirstRender();
	const isMobile = useWindowSize().isMobile;
	let hasVenueReportsShowPermission = useSelector(hasPermission("VENUE_REPORTS_SHOW"));
	const {
		didDashboardSegmentChange,
		segmentOptionClickHandler,
		createDashboardSegment,
		saveDashboardSegment,
		didDashboardSegmnetGridConfigChange,
	} = useDashboardSegment();
	const { t } = useTranslation();
	const nextDataLoading = rerenderDashboard && loading && !isFirstRender;
	if (dashboardType === DashboardType.GROUP_ORGANIZATIONS) hasVenueReportsShowPermission = true;
	const displayEmptyList = !hasVenueReportsShowPermission && dashboardType === DashboardType.ORGANIZATION;
	const confirmation = useConfirmation();
	const { addFlash } = useFlash();

	useEffect(() => {
		fetchDataAndApplyNewDataToDashboardState();
	}, []);

	const saveCurrentConfigHandler = async () => {
		try {
			if (selectedSegment.slug === "default") {
				setShowSegmentModal(true);
			} else {
				await confirmation({
					title: t("common.word.confirmation", { ns: "lib" }),
					message: t("modules.dashboard.field.save_current_config_confirmation.title"),
				});

				await saveDashboardSegment(selectedSegment, true);
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const buttons: ButtonProps[] = [
		{
			content: (
				<div className="ms-2" key="segment">
					<Segment.List
						segments={allSegments}
						selected={selectedSegment.slug}
						onChange={segmentOptionClickHandler}
						disabled={isEditMode}
					/>
				</div>
			),
		},
		{
			title: t("go_segment.action.save", { ns: "lib" }),
			variant: "success",
			action: () => setShowSegmentModal(true),
			hide: isEditMode || !didDashboardSegmentChange(),
		},
		{
			title: t("modules.dashboard.action.save_current_config.title"),
			variant: "success",
			action: saveCurrentConfigHandler,
			hide: !isEditMode || !didDashboardSegmnetGridConfigChange(),
		},
		{
			content: <EditModeTriggerButton key="editButton" />,
		},
	];

	if (loading) {
		if (isFirstRender || !state.data) {
			return <LoadingContainer />;
		}
	}

	if (displayEmptyList) {
		return (
			<div className="d-flex justify-content-center align-items-center w-100">
				<EmptyList title={t("modules.dashboard.field.lack_of_permissions.title")} />
			</div>
		);
	}

	return (
		<>
			<div className={`content ${isMobile ? "" : "pb-2"}`}>
				{!isMobile && (
					<div className="dashboard-header">
						<Header title={t("modules.dashboard.header.title")} buttons={buttons} />
						<div className="d-flex gap-4">
							<ReportLastUpdateInfo lastUpdateDate={state?.data?.last_update} />
						</div>
					</div>
				)}
				<MobileActions>
					<ReportLastUpdateInfo lastUpdateDate={state?.data?.last_update} />
				</MobileActions>
				<div className={"dashboard"}>
					<div className={"content-preview"}>
						<DashboardFilters />
						{isMobile && <DashboardTabs setActiveTab={setActiveTab} activeTab={activeTab} />}
						<div className="dashboard-content">
							{nextDataLoading && <LoadingSVG className={"loading-custom"} style={{ top: "40%" }} />}
							<DraggableDashboardMainContent activeTab={activeTab} nextDataLoading={nextDataLoading} />
						</div>
					</div>
				</div>
			</div>
			{showSegmentModal && (
				<Segment.SaveModal
					segment={selectedSegment}
					onHide={() => setShowSegmentModal(false)}
					onSave={async (segment: SegmentType) => {
						try {
							const updatedSegment = await saveDashboardSegment(segment);
							setShowSegmentModal(false);
							return updatedSegment;
						} catch (err) {
							return handleException(err);
						}
					}}
					onCreate={async (segmentName: string) => {
						try {
							const newSegment = await createDashboardSegment(segmentName);
							setShowSegmentModal(false);
							return newSegment;
						} catch (err) {
							return handleException(err);
						}
					}}
				/>
			)}
		</>
	);
};
export default Dashboard;
